<script>
	import { onMount, onDestroy } from "svelte";
	import { getApiBase, getCurrentStore, fetchOrders, updateOrderStatus, updateOrderPaymentStatus, fetchCouriers, dispatchOrderWithCourier, updateTableBatchStatus, getTableReceiptUrl } from "../services/api";
	import Icon from "../components/icons/Icon.svelte";

	export let onPrintOrder = (order) => {};
	export let onPrintTableReceipt = null;
	export let onNotifyClient = (order, msg) => {};

	const statusMap = {
		pending: 'analysis',
		in_prep: 'kitchen',
		out_for_delivery: 'delivery',
		delivered: 'done',
		cancelled: 'done'
	};

	const reverseStatusMap = {
		analysis: 'pending',
		kitchen: 'in_prep',
		delivery: 'out_for_delivery',
		done: 'delivered'
	};

	let orders = [];
	let socket = null;
	let isSocketConnected = false;
	let contingencyInterval = null;
	let lastFetchTimestamp = 0;
	let draggedOrder = null;
	let dragOverCol = null;
	let isDragging = false;
	let selectedOrderModal = null;

	// Gestão de Despacho com Roleta de Motoboy
	let showDispatchModal = false;
	let orderToDispatch = null;
	let couriersList = [];
	let selectedMotoboyId = "";
	let isDispatching = false;

	function formatCurrency(val) {
		const num = parseFloat(val);
		return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
	}

	function playNotificationSound() {
		try {
			const ctx = new (window.AudioContext || window.webkitAudioContext)();
			const notes = [
				{ freq: 659.25, time: 0.00, dur: 0.8 }, // E5
				{ freq: 830.61, time: 0.10, dur: 0.9 }, // G#5
				{ freq: 987.77, time: 0.22, dur: 1.2 }  // B5 (Campainha principal)
			];

			notes.forEach(n => {
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'triangle';
				osc.frequency.setValueAtTime(n.freq, ctx.currentTime + n.time);

				gain.gain.setValueAtTime(0.001, ctx.currentTime + n.time);
				gain.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + n.time + 0.02);
				gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + n.time + n.dur);

				osc.connect(gain);
				gain.connect(ctx.destination);

				osc.start(ctx.currentTime + n.time);
				osc.stop(ctx.currentTime + n.time + n.dur);
			});
		} catch (e) {}
	}

	function formatOrderFromBackend(o) {
		let itemsFormatted = [];
		let rawItemsList = Array.isArray(o.items) ? o.items : [];
		if (!rawItemsList.length && o.items_json) {
			try {
				rawItemsList = typeof o.items_json === 'string' ? JSON.parse(o.items_json) : o.items_json;
				if (!Array.isArray(rawItemsList)) rawItemsList = [];
			} catch (_) {}
		}
		const hasEditedItem = rawItemsList.some(it => it && (it.isEdited || it.editedAt));

		if (rawItemsList.length > 0) {
			itemsFormatted = rawItemsList.map(it => {
				const qty = it.qty || it.quantity || 1;
				let name = it.fullName || it.name || 'Item';
				const isHalf = Boolean(it.isHalfAndHalf || it.is_half_and_half || it.split_type === 'half' || it.second_flavor_name || it.second_flavor_id || it.secondProductId);
				const f1 = it.flavor1Name || it.flavor1_name || null;
				const f2 = it.flavor2Name || it.flavor2_name || it.second_flavor_name || it.secondFlavorName || null;
				if (isHalf && f1 && f2 && !name.includes('1/2')) {
					name = `Pizza Meio a Meio (1/2 ${f1} + 1/2 ${f2})`;
				} else if (isHalf && !f1 && f2 && !name.includes('1/2')) {
					const baseClean = (it.name || '').replace(/^pizza/i, '').replace(/meio\s*a\s*meio/i, '').trim() || 'Sabor 1';
					name = `Pizza Meio a Meio (1/2 ${baseClean} + 1/2 ${f2})`;
				}
				const sizeName = it.size || it.size_name || it.sizeName || null;
				if (sizeName && !name.toLowerCase().includes(sizeName.toLowerCase())) {
					const shortSize = sizeName.split(' ')[0];
					if (!name.toLowerCase().includes(shortSize.toLowerCase())) {
						name = `${name} [${sizeName}]`;
					}
				}
				let desc = `${qty}x ${name}`;
				const rawMods = Array.isArray(it.modifiers) && it.modifiers.length > 0 
					? it.modifiers 
					: (Array.isArray(it.complements) ? it.complements : []);
				const nonSizeMods = rawMods.filter(m => !m.is_size && (!sizeName || !(m.name || m).toLowerCase().includes(sizeName.toLowerCase())));
				if (nonSizeMods.length > 0) {
					const mods = nonSizeMods.map(m => m.name || m.label || m).filter(Boolean).join(', ');
					if (mods) desc += ` (+ ${mods})`;
				}
				if (it.notes) {
					desc += ` [Obs: ${it.notes}]`;
				}
				if (it.isEdited) {
					desc += ` (✏️ Alterado)`;
				}
				return desc;
			});
		} else {
			itemsFormatted = (o.source === 'dine_in' || (o.delivery_address && o.delivery_address.toLowerCase().includes('salão')))
				? ["1x Pedido Salão"]
				: ["1x Pedido WhatsApp"];
		}

		let createdTime = "Agora";
		if (o.created_at) {
			const d = new Date(o.created_at);
			createdTime = !isNaN(d.getTime()) ? d.toLocaleTimeString().slice(0, 5) : "Agora";
		}

		const isDineInBatch = o.type === 'dine_in_batch' || o.isDineInBatch;
		const isDineIn = isDineInBatch || o.source === 'dine_in' || (o.delivery_address && o.delivery_address.toLowerCase().includes('salão'));
		const isWeb = o.source === 'web' || o.source === 'cardapio' || o.source === 'web_menu';
		
		let defaultClient = "Cliente WhatsApp";
		if (isDineInBatch) defaultClient = o.tableLabel || `Mesa ${o.tableNumber || ''}`;
		else if (isDineIn) defaultClient = "Cliente Salão";
		else if (isWeb) defaultClient = "Cliente Web";

		const clientName = (o.customer_name && o.customer_name.trim()) ? o.customer_name : defaultClient;

		let paymentFormatted = o.payment_method || "PIX";
		if (isDineInBatch) {
			paymentFormatted = "Consumo Salão";
		} else if (!isDineIn && !isWeb && !paymentFormatted.toLowerCase().includes('whatsapp')) {
			paymentFormatted = `${paymentFormatted} (WhatsApp)`;
		} else if (isDineIn && !paymentFormatted.toLowerCase().includes('salão')) {
			paymentFormatted = `${paymentFormatted} (Salão)`;
		} else if (isWeb && !paymentFormatted.toLowerCase().includes('site') && !paymentFormatted.toLowerCase().includes('web') && !paymentFormatted.toLowerCase().includes('cardápio')) {
			paymentFormatted = `${paymentFormatted} (Cardápio Online)`;
		}

		// Dine-in batches entram direto na Cozinha ('kitchen') e quando prontos vão para 'done'
		let assignedStatus = statusMap[o.status] || 'analysis';
		if (isDineInBatch) {
			assignedStatus = (o.rawStatus === 'ready' || o.status === 'delivered') ? 'done' : 'kitchen';
		}

		return {
			id: String(o.order_number || o.id),
			rawId: o.id,
			type: o.type || 'order',
			isDineInBatch,
			hasEditedItem,
			batchId: o.batchId || null,
			tableId: o.tableId || null,
			tableLabel: o.tableLabel || (isDineInBatch ? clientName : null),
			tableNumber: o.tableNumber || null,
			client: clientName,
			phone: o.customer_phone || "",
			items: itemsFormatted,
			rawItems: rawItemsList,
			total: parseFloat(o.total || 0),
			subtotal: parseFloat(o.subtotal || o.total || 0),
			deliveryFee: parseFloat(o.delivery_fee || 0),
			discount: parseFloat(o.discount || 0),
			payment: paymentFormatted,
			rawPayment: o.payment_method || "PIX",
			payment_status: o.payment_status || 'pending',
			paid_at: o.paid_at || null,
			pix_payment_id: o.pix_payment_id || null,
			status: assignedStatus,
			rawStatus: o.rawStatus || o.status,
			source: o.source || (isDineIn ? 'dine_in' : 'whatsapp'),
			isDineIn,
			isWeb,
			time: createdTime,
			createdAt: o.created_at || null,
			updatedAt: o.updated_at || null,
			obs: o.delivery_address || ""
		};
	}

	export let activeOrdersCount = 0;

	// Gestão de Concluídos: auto-recolhe pedidos após 2 horas ou de dias anteriores para manter o painel limpo
	let showAllCompleted = false;

	function isRecentDoneOrder(order) {
		if (!order) return false;
		const timestamp = order.updatedAt || order.createdAt;
		if (!timestamp) return true;
		const orderDate = new Date(timestamp);
		if (isNaN(orderDate.getTime())) return true;

		const now = new Date();
		// 1. Mesmo dia local do operador
		const isSameDay = orderDate.getFullYear() === now.getFullYear() &&
		                  orderDate.getMonth() === now.getMonth() &&
		                  orderDate.getDate() === now.getDate();
		if (!isSameDay) return false;

		// 2. Janela de 2 horas (120 minutos)
		const diffMs = now.getTime() - orderDate.getTime();
		const twoHoursMs = 2 * 60 * 60 * 1000;
		return diffMs <= twoHoursMs;
	}

	$: allDoneOrders = orders.filter(o => o.status === 'done');
	$: recentDoneOrders = allDoneOrders.filter(isRecentDoneOrder);
	$: archivedDoneCount = allDoneOrders.length - recentDoneOrders.length;
	$: visibleDoneOrders = showAllCompleted ? allDoneOrders : recentDoneOrders;

	// Métricas Dinâmicas e Reais baseadas nos pedidos da loja (Lotes de mesa não duplicam receita antes de fechar)
	$: validOrders = orders.filter(o => o.status !== 'cancelled' && o.rawStatus !== 'cancelled');
	$: deliveryOrders = validOrders.filter(o => !o.isDineInBatch);
	$: totalOrdersCount = deliveryOrders.length;
	$: totalRevenue = deliveryOrders.reduce((acc, o) => acc + (Number(o.total) || 0), 0);
	$: averageTicket = totalOrdersCount > 0 ? (totalRevenue / totalOrdersCount) : 0;
	$: activeOrdersCount = orders.filter(o => o.status === 'analysis' || o.status === 'kitchen' || o.status === 'delivery').length;

	$: averageDeliveryMinutes = (() => {
		const completed = validOrders.filter(o => o.status === 'done' && o.createdAt);
		if (completed.length > 0) {
			let totalMins = 0;
			let count = 0;
			for (const o of completed) {
				if (o.updatedAt && o.createdAt) {
					const diff = (new Date(o.updatedAt).getTime() - new Date(o.createdAt).getTime()) / 60000;
					if (diff > 1 && diff < 240) {
						totalMins += diff;
						count++;
					}
				}
			}
			if (count > 0) return Math.round(totalMins / count);
		}
		const inPrep = validOrders.filter(o => o.status === 'kitchen' || o.status === 'analysis').length;
		return inPrep > 0 ? Math.min(45, 18 + inPrep * 4) : 22;
	})();

	function stopContingencyPolling() {
		if (contingencyInterval) {
			clearInterval(contingencyInterval);
			contingencyInterval = null;
		}
	}

	function startContingencyPolling(intervalMs = 12000) {
		stopContingencyPolling();
		contingencyInterval = setInterval(() => {
			if (!isSocketConnected) {
				loadOrdersList();
			}
		}, intervalMs);
	}

	function onWindowFocus() {
		const now = Date.now();
		if (now - lastFetchTimestamp > 30000) {
			loadOrdersList();
		}
	}

	async function loadOrdersList() {
		lastFetchTimestamp = Date.now();
		try {
			const data = await fetchOrders();
			if (Array.isArray(data)) {
				orders = data.map(formatOrderFromBackend);
				if (selectedOrderModal) {
					const updated = orders.find(o => o.rawId === selectedOrderModal.rawId || o.id === selectedOrderModal.id);
					if (updated) selectedOrderModal = updated;
				}
			}
		} catch (e) {
			console.warn("Falha ao carregar pedidos:", e);
		}
		loadReconciliations();
	}

	function handleNewOrderDelta(payload) {
		const rawOrder = payload?.order;
		if (!rawOrder || (!rawOrder.id && !rawOrder.order_number)) {
			loadOrdersList();
			return;
		}

		if (payload.playBell !== false) {
			playNotificationSound();
		}

		if (payload.shouldPrint && onPrintOrder) {
			try {
				onPrintOrder(rawOrder);
			} catch (_) {}
		}

		const formatted = formatOrderFromBackend(rawOrder);

		const existingIdx = orders.findIndex(o => 
			(formatted.rawId && (o.rawId === formatted.rawId || o.id === String(formatted.rawId))) ||
			(formatted.id && (String(o.id) === String(formatted.id) || String(o.rawId) === String(formatted.id)))
		);

		if (existingIdx !== -1) {
			orders[existingIdx] = formatted;
			orders = [...orders];
		} else {
			orders = [formatted, ...orders];
		}

		if (selectedOrderModal && (selectedOrderModal.rawId === formatted.rawId || selectedOrderModal.id === formatted.id)) {
			selectedOrderModal = formatted;
		}
	}

	function handleOrderStatusDelta(payload) {
		if (!payload || (!payload.orderId && !payload.orderNumber)) {
			loadOrdersList();
			return;
		}

		const targetId = payload.orderId;
		const targetNumber = payload.orderNumber;
		const newRawStatus = payload.status;

		const existingIdx = orders.findIndex(o => 
			(targetId && (o.rawId == targetId || o.id == targetId)) ||
			(targetNumber && (o.id == targetNumber || o.rawId == targetNumber))
		);

		if (existingIdx !== -1) {
			if (payload.order && typeof payload.order === 'object') {
				orders[existingIdx] = formatOrderFromBackend(payload.order);
			} else if (newRawStatus) {
				const assigned = statusMap[newRawStatus] || 'analysis';
				orders[existingIdx].status = assigned;
				orders[existingIdx].rawStatus = newRawStatus;
				orders[existingIdx].updatedAt = new Date().toISOString();
			}
			orders = [...orders];

			if (selectedOrderModal && (selectedOrderModal.rawId == targetId || selectedOrderModal.id == targetId || selectedOrderModal.id == targetNumber)) {
				selectedOrderModal = orders[existingIdx];
			}
		} else {
			loadOrdersList();
		}
	}

	function handleOrderPaidDelta(payload) {
		if (!payload || (!payload.orderId && !payload.order_id && !payload.orderNumber)) {
			loadOrdersList();
			return;
		}

		const targetId = payload.orderId || payload.order_id;
		const targetNumber = payload.orderNumber || payload.order_number;

		const existingIdx = orders.findIndex(o => 
			(targetId && (o.rawId == targetId || o.id == targetId)) ||
			(targetNumber && (o.id == targetNumber || o.rawId == targetNumber))
		);

		if (existingIdx !== -1) {
			if (payload.order && typeof payload.order === 'object') {
				orders[existingIdx] = formatOrderFromBackend(payload.order);
			} else {
				orders[existingIdx].payment_status = 'paid';
				orders[existingIdx].paid_at = payload.paid_at || payload.paidAt || new Date().toISOString();
				const pixId = payload.pix_payment_id || payload.paymentId || payload.pixPaymentId;
				if (pixId) orders[existingIdx].pix_payment_id = pixId;
			}
			orders = [...orders];

			if (selectedOrderModal && (selectedOrderModal.rawId == targetId || selectedOrderModal.id == targetId)) {
				selectedOrderModal = orders[existingIdx];
			}
		} else {
			loadOrdersList();
		}
	}

	let reconciliationAlerts = [];
	let resolvingAlertIds = [];

	async function loadReconciliations() {
		try {
			const token = localStorage.getItem("matafome_token") || "";
			const res = await fetch(`${getApiBase()}/api/orders/reconciliations?status=pending`, {
				headers: {
					"Authorization": `Bearer ${token}`
				}
			});
			if (res.ok) {
				const data = await res.json();
				reconciliationAlerts = data.reconciliations || [];
			}
		} catch (err) {
			console.warn("Erro ao carregar conciliações PIX:", err);
		}
	}

	async function resolveReconciliation(id, actionStatus) {
		if (resolvingAlertIds.includes(id)) {
			return; // Impede múltiplos cliques e submissões concorrentes no frontend
		}
		resolvingAlertIds = [...resolvingAlertIds, id];

		try {
			const token = localStorage.getItem("matafome_token") || "";
			const res = await fetch(`${getApiBase()}/api/orders/reconciliations/${encodeURIComponent(id)}/resolve`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`
				},
				body: JSON.stringify({
					status: actionStatus,
					notes: actionStatus === 'refunded' ? 'Estorno registrado manualmente pelo operador' : 'Pagamento verificado pelo operador'
				})
			});
			if (res.ok) {
				await loadReconciliations();
			} else if (res.status === 409) {
				// Conflito: outra sessão já resolveu esta ocorrência
				await loadReconciliations();
			} else if (res.status === 401 || res.status === 403) {
				console.error("Permissão insuficiente para resolver ocorrência:", res.status);
			} else if (res.status === 404) {
				// Ocorrência não mais existente
				await loadReconciliations();
			}
		} catch (err) {
			console.error("Erro ao resolver conciliação:", err);
		} finally {
			resolvingAlertIds = resolvingAlertIds.filter(item => item !== id);
		}
	}


	function playStepSound() {
		try {
			const ctx = new (window.AudioContext || window.webkitAudioContext)();
			const osc = ctx.createOscillator();
			const gain = ctx.createGain();
			osc.type = 'triangle';
			osc.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
			osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.07); // E5
			osc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.14); // G5
			gain.gain.setValueAtTime(0.35, ctx.currentTime);
			gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
			osc.connect(gain);
			gain.connect(ctx.destination);
			osc.start();
			osc.stop(ctx.currentTime + 0.35);
		} catch (e) {}
	}

	function handleDragStart(e, order) {
		if (e.target.closest('button')) {
			e.preventDefault();
			return;
		}
		isDragging = true;
		draggedOrder = order;
		if (e.dataTransfer) {
			e.dataTransfer.setData('text/plain', String(order.rawId || order.id));
			e.dataTransfer.effectAllowed = 'move';
		}
	}

	function handleDragEnd() {
		draggedOrder = null;
		dragOverCol = null;
		setTimeout(() => { isDragging = false; }, 60);
	}

	function handleDragOver(e) {
		e.preventDefault();
		if (e.dataTransfer) {
			e.dataTransfer.dropEffect = 'move';
		}
	}

	function handleDragEnter(e, colName) {
		e.preventDefault();
		dragOverCol = colName;
	}

	function handleDragLeave(e, colName) {
		if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
		if (dragOverCol === colName) {
			dragOverCol = null;
		}
	}

	function handleDrop(e, targetStatus) {
		e.preventDefault();
		e.stopPropagation();
		dragOverCol = null;
		let orderId = null;
		if (e.dataTransfer) {
			try {
				orderId = e.dataTransfer.getData('text/plain');
			} catch (err) {}
		}
		if (!orderId && draggedOrder) {
			orderId = draggedOrder.rawId || draggedOrder.id;
		}
		if (orderId) {
			moveOrder(orderId, targetStatus);
		}
		draggedOrder = null;
		setTimeout(() => { isDragging = false; }, 60);
	}

	function handleCardClick(order, e) {
		if (isDragging) return;
		if (e.target.closest('button')) return;
		selectedOrderModal = order;
	}

	function closeOrderModal() {
		selectedOrderModal = null;
	}

	async function moveOrder(orderId, newStatus) {
		const order = orders.find(o => 
			o.rawId === orderId || 
			o.id === orderId || 
			String(o.id) === String(orderId) || 
			String(o.rawId) === String(orderId)
		);
		if (!order || order.status === newStatus) return;

		// Se for lote de produção de salão (dine-in batch)
		if (order.isDineInBatch) {
			await executeMoveBatch(order, newStatus);
			return;
		}

		// Se o pedido está saindo para entrega e é de delivery sem motoboy vinculado ainda
		if (newStatus === 'delivery' && !order.isDineIn && !order.motoboy_id) {
			try {
				const couriers = await fetchCouriers();
				if (couriers && couriers.length > 0) {
					orderToDispatch = order;
					couriersList = couriers;
					const available = couriers.filter(c => c.status === 'available').sort((a, b) => a.queue_position - b.queue_position);
					selectedMotoboyId = available.length > 0 ? available[0].id : couriers[0].id;
					showDispatchModal = true;
					return;
				}
			} catch (e) {
				console.warn("Falha ao buscar motoboys para despacho:", e);
			}
		}

		await executeMoveOrder(order, newStatus);
	}

	async function executeMoveBatch(order, targetKanbanStatus) {
		const prevStatus = order.status;
		const targetStatus = (targetKanbanStatus === 'kitchen') ? 'kitchen' : 'done';
		const backendStatus = (targetStatus === 'done') ? 'ready' : 'in_prep';

		orders = orders.map(o => (
			o.rawId === order.rawId || 
			o.id === order.id || 
			String(o.id) === String(order.id) || 
			String(o.rawId) === String(order.rawId)
		) ? { 
			...o, 
			status: targetStatus,
			rawStatus: backendStatus
		} : o);

		if (selectedOrderModal && (selectedOrderModal.rawId === order.rawId || selectedOrderModal.id === order.id)) {
			selectedOrderModal = { ...selectedOrderModal, status: targetStatus, rawStatus: backendStatus };
		}

		playStepSound();

		try {
			await updateTableBatchStatus(order.tableId, order.batchId, backendStatus);
		} catch (err) {
			console.error("Falha ao atualizar status do lote na cozinha:", err);
			orders = orders.map(o => (
				o.rawId === order.rawId || o.id === order.id
			) ? { ...o, status: prevStatus } : o);
		}
	}

	async function executeMoveOrder(order, newStatus, motoboyId = null, motoboyName = null) {
		const prevStatus = order.status;
		orders = orders.map(o => (
			o.rawId === order.rawId || 
			o.id === order.id || 
			String(o.id) === String(order.id) || 
			String(o.rawId) === String(order.rawId)
		) ? { 
			...o, 
			status: newStatus,
			updatedAt: new Date().toISOString(),
			motoboy: motoboyName || o.motoboy,
			motoboy_id: motoboyId || o.motoboy_id
		} : o);

		if (selectedOrderModal && (selectedOrderModal.rawId === order.rawId || selectedOrderModal.id === order.id)) {
			selectedOrderModal = { 
				...selectedOrderModal, 
				status: newStatus,
				motoboy: motoboyName || selectedOrderModal.motoboy,
				motoboy_id: motoboyId || selectedOrderModal.motoboy_id
			};
		}
		
		playStepSound();

		// Notifica o cliente automaticamente no WhatsApp
		const backendStatus = reverseStatusMap[newStatus];
		if (backendStatus && onNotifyClient) {
			const statusDescriptions = {
				in_prep: "Pedido aceito e em preparação na cozinha!",
				out_for_delivery: motoboyName ? `Pedido saiu para entrega com o motoboy ${motoboyName}!` : "Pedido saiu para entrega com o motoboy!",
				delivered: "Pedido entregue com sucesso! Bom apetite!"
			};
			if (statusDescriptions[backendStatus]) {
				try {
					onNotifyClient(order, `Status do Pedido #${order.id} atualizado: ${statusDescriptions[backendStatus]}`);
				} catch (err) {}
			}
		}

		try {
			const targetId = order.rawId || order.id;
			if (motoboyId) {
				await dispatchOrderWithCourier(targetId, motoboyId);
			} else {
				await updateOrderStatus(targetId, backendStatus);
			}
		} catch (err) {
			console.error("Falha ao sincronizar status do pedido:", err);
			orders = orders.map(o => (
				o.rawId === order.rawId || 
				o.id === order.id
			) ? { ...o, status: prevStatus } : o);
			if (selectedOrderModal && (selectedOrderModal.rawId === order.rawId || selectedOrderModal.id === order.id)) {
				selectedOrderModal = { ...selectedOrderModal, status: prevStatus };
			}
			if (typeof alert === 'function') {
				alert(`Aviso: ${err.message || 'Não foi possível atualizar o status do pedido.'}`);
			}
		}
	}

	async function confirmDispatch() {
		if (!orderToDispatch) return;
		isDispatching = true;
		try {
			const courier = couriersList.find(c => c.id === selectedMotoboyId);
			const motoboyName = courier ? courier.name : 'Entregador';
			await executeMoveOrder(orderToDispatch, 'delivery', selectedMotoboyId, motoboyName);
			showDispatchModal = false;
			orderToDispatch = null;
		} catch (err) {
			console.error("Erro ao despachar:", err);
			if (typeof alert === 'function') {
				alert(`Aviso: ${err.message || 'Erro ao despachar pedido.'}`);
			}
		} finally {
			isDispatching = false;
		}
	}

	async function dispatchWithoutMotoboy() {
		if (!orderToDispatch) return;
		await executeMoveOrder(orderToDispatch, 'delivery');
		showDispatchModal = false;
		orderToDispatch = null;
	}

	function closeDispatchModal() {
		showDispatchModal = false;
		orderToDispatch = null;
	}

	function handlePrint(order) {
		if (onPrintOrder) {
			onPrintOrder(order);
		}
	}

	function handlePrintRectification(order, item) {
		if (!order || !order.tableId) return;
		const targetItem = item || (order.rawItems || []).find(it => it && (it.lastChange || (Array.isArray(it.productionChanges) && it.productionChanges.length > 0) || it.isEdited));
		const itemId = targetItem?.id || null;
		const changeId = targetItem?.lastChange?.changeId || (Array.isArray(targetItem?.productionChanges) && targetItem.productionChanges.length > 0 ? targetItem.productionChanges[targetItem.productionChanges.length - 1].changeId : null);

		if (onPrintTableReceipt) {
			onPrintTableReceipt({
				tableId: order.tableId,
				type: 'rectification',
				itemId,
				changeId
			});
		} else {
			const url = getTableReceiptUrl(order.tableId, { type: 'rectification', itemId, changeId });
			window.open(url, '_blank');
		}
	}

	async function toggleOrderPaymentStatus(order) {
		const newStatus = order.payment_status === 'paid' ? 'pending' : 'paid';
		const targetId = order.rawId || order.id;

		try {
			await updateOrderPaymentStatus(targetId, newStatus);
			order.payment_status = newStatus;
			if (selectedOrderModal && (selectedOrderModal.rawId === targetId || selectedOrderModal.id === targetId)) {
				selectedOrderModal = { ...selectedOrderModal, payment_status: newStatus };
			}
			orders = orders.map(o => (o.rawId === targetId || o.id === targetId) ? { ...o, payment_status: newStatus } : o);
			playStepSound();
		} catch (err) {
			console.error("Erro ao alterar status de pagamento:", err);
		}
	}

	const onGlobalDragEnd = () => {
		draggedOrder = null;
		dragOverCol = null;
		setTimeout(() => { isDragging = false; }, 60);
	};

	onMount(() => {
		loadOrdersList();

		if (typeof window !== "undefined") {
			window.addEventListener('dragend', onGlobalDragEnd);
			window.addEventListener('drop', onGlobalDragEnd);
			window.addEventListener('focus', onWindowFocus);

			if (window.io) {
				try {
					socket = window.io(getApiBase());

					socket.on("connect", () => {
						isSocketConnected = true;
						stopContingencyPolling();
						const store = getCurrentStore();
						const token = localStorage.getItem("matafome_token") || "";
						if (store?.id) {
							socket.emit("join_store", { 
								storeId: store.id,
								accessKey: store.access_key,
								token: token
							});
						}
						// Sincronização pontual ao conectar/reconectar para recuperar eventuais perdas offline
						loadOrdersList();
					});

					socket.on("disconnect", () => {
						isSocketConnected = false;
						startContingencyPolling(12000);
					});

					socket.on("connect_error", () => {
						isSocketConnected = false;
						startContingencyPolling(12000);
					});

					socket.on("order_status_updated", (payload) => {
						handleOrderStatusDelta(payload);
					});

					socket.on("order_paid", (payload) => {
						handleOrderPaidDelta(payload);
					});

					socket.on("new_order", (payload) => {
						handleNewOrderDelta(payload);
					});

					socket.on("kitchen_ticket_created", () => {
						playNotificationSound();
						loadOrdersList();
					});

					socket.on("kitchen_ticket_updated", () => {
						loadOrdersList();
					});

					socket.on("table_updated", () => {
						loadOrdersList();
					});

					socket.on("order_payment_reconciliation_required", () => {
						playNotificationSound();
						loadReconciliations();
					});

					socket.on("order_payment_reconciliation_resolved", () => {
						loadReconciliations();
					});
				} catch (err) {
					console.warn("Socket realtime indisponível:", err);
					isSocketConnected = false;
					startContingencyPolling(10000);
				}
			} else {
				startContingencyPolling(10000);
			}
		}
	});

	onDestroy(() => {
		stopContingencyPolling();
		if (socket) socket.disconnect();
		if (typeof window !== 'undefined') {
			window.removeEventListener('dragend', onGlobalDragEnd);
			window.removeEventListener('drop', onGlobalDragEnd);
			window.removeEventListener('focus', onWindowFocus);
		}
	});
</script>

<div class="kanban-wrapper">
	<!-- Banner Operacional de Conciliação Financeira de PIX Tardio (Fase 3.10) -->
	{#if reconciliationAlerts && reconciliationAlerts.length > 0}
		<div class="reconciliation-alert-container">
			{#each reconciliationAlerts as alert (alert.id)}
				<div class="reconciliation-card">
					<div class="rec-icon">⚠️</div>
					<div class="rec-body">
						<div class="rec-title">PAGAMENTO PIX RECEBIDO PARA PEDIDO CANCELADO!</div>
						<div class="rec-desc">
							Pedido <strong>#{alert.order_number}</strong> recebeu <strong>R$ {parseFloat(alert.amount || 0).toFixed(2)}</strong> (PIX ID: {alert.payment_id}).
							O pedido foi cancelado por expiração antes da confirmação. Verifique com o cliente.
						</div>
					</div>
					<div class="rec-actions">
						<button
							class="btn-rec-resolve"
							disabled={resolvingAlertIds.includes(alert.id)}
							on:click={() => resolveReconciliation(alert.id, 'resolved')}
						>
							{#if resolvingAlertIds.includes(alert.id)}
								⏳ Processando...
							{:else}
								✓ Marcar como Tratado
							{/if}
						</button>
						<button
							class="btn-rec-refund"
							disabled={resolvingAlertIds.includes(alert.id)}
							on:click={() => resolveReconciliation(alert.id, 'refunded')}
						>
							{#if resolvingAlertIds.includes(alert.id)}
								⏳ Processando...
							{:else}
								↩ Registrar Estorno
							{/if}
						</button>
					</div>
				</div>
			{/each}
		</div>
	{/if}

	<!-- Barra Superior de Métricas Operacionais -->
	<div class="kanban-header">
		<div class="title-area">
			<h2>Gestor de Pedidos ao Vivo</h2>
			<span class="live-pill"><span class="dot"></span> Atualização em Tempo Real</span>
		</div>

		<div class="metrics-row">
			<div class="metric-card">
				<span class="metric-label">Faturamento Hoje</span>
				<span class="metric-value">R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
			</div>
			<div class="metric-card">
				<span class="metric-label">Pedidos Hoje</span>
				<span class="metric-value">{totalOrdersCount} {totalOrdersCount === 1 ? 'pedido' : 'pedidos'}</span>
			</div>
			<div class="metric-card">
				<span class="metric-label">Tempo Médio</span>
				<span class="metric-value green">{averageDeliveryMinutes} minutos</span>
			</div>
			<div class="metric-card">
				<span class="metric-label">Ticket Médio</span>
				<span class="metric-value">R$ {averageTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
			</div>
		</div>
	</div>

	<!-- Colunas do Kanban -->
	<div class="kanban-board">
		<!-- Coluna 1: Novos / Em Análise -->
		<div 
			class="column col-analysis" 
			class:drag-over={dragOverCol === 'analysis'}
			on:dragover={handleDragOver} 
			on:dragenter={(e) => handleDragEnter(e, 'analysis')}
			on:dragleave={(e) => handleDragLeave(e, 'analysis')}
			on:drop={(e) => handleDrop(e, 'analysis')}
		>
			<div class="col-header">
				<div class="col-title">
					<span class="status-indicator yellow"></span>
					<h3>Em Análise</h3>
				</div>
				<span class="count-pill">{orders.filter(o => o.status === 'analysis').length}</span>
			</div>
			<div 
				class="cards-list"
				on:dragover={handleDragOver} 
				on:dragenter={(e) => handleDragEnter(e, 'analysis')}
				on:drop={(e) => handleDrop(e, 'analysis')}
			>
				{#each orders.filter(o => o.status === 'analysis') as order (order.rawId || order.id)}
					<div 
						class="order-card pulse-border" 
						class:is-being-dragged={draggedOrder && (draggedOrder.rawId === order.rawId || draggedOrder.id === order.id)}
						draggable="true" 
						on:dragstart={(e) => handleDragStart(e, order)}
						on:dragend={handleDragEnd}
						on:click={(e) => handleCardClick(order, e)}
					>
						<div class="card-top">
							<span class="order-id">#{order.id}</span>
							<div class="card-top-badges">
								{#if order.hasEditedItem}
									<span class="origin-badge dine-in-edited" title="Item alterado pelo salão">⚠️ Alterado</span>
								{/if}
								{#if order.isDineIn}
									<span class="origin-badge dine-in" title="Consumo no Salão / Mesas"><Icon name="utensils" size={11} /> Salão</span>
								{:else if order.isWeb}
									<span class="origin-badge web" title="Cardápio Digital Web"><Icon name="globe" size={11} /> Web</span>
								{:else}
									<span class="origin-badge whatsapp" title="Pedido via WhatsApp"><Icon name="whatsapp" size={11} color="#10B981" /> WhatsApp</span>
								{/if}
								{#if order.payment_status === 'paid'}
									<span class="origin-badge pix-paid" title="PIX 100% Pago Online (Anti-Fraude)"><Icon name="check-circle" size={11} /> PIX Pago</span>
								{:else if order.rawPayment && order.rawPayment.toUpperCase().includes('PIX')}
									<span class="origin-badge pix-pending" title="Aguardando Confirmação de PIX"><Icon name="clock" size={11} /> PIX Pendente</span>
								{/if}
								<span class="order-time">{order.time}</span>
							</div>
						</div>

						<div class="client-row">
							<span class="client-name">{order.client}</span>
							{#if order.phone && !order.isDineIn}
								<span class="client-phone">{order.phone}</span>
							{/if}
						</div>

						{#if order.hasEditedItem}
							<div class="edited-item-alert-banner" style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
								<span>⚠️ ITEM ALTERADO PELO SALÃO</span>
								{#if order.isDineInBatch}
									<button 
										type="button" 
										class="btn-rectify-card"
										on:mousedown|stopPropagation
										on:click|stopPropagation={() => handlePrintRectification(order)}
										title="Imprimir Retificação de Produção"
										style="background: #B45309; color: #fff; border: none; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; cursor: pointer; display: inline-flex; align-items: center; gap: 2px;"
									>
										🖨️ Retificação
									</button>
								{/if}
							</div>
						{/if}

						<div class="items-summary">
							{#each order.items.slice(0, 3) as item}
								<div class="item-line">• {item}</div>
							{/each}
							{#if order.items.length > 3}
								<div class="item-more">+ {order.items.length - 3} mais...</div>
							{/if}
						</div>

						{#if order.obs}
							<div class="obs-box">
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
									<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
									<circle cx="12" cy="10" r="3"></circle>
								</svg>
								<span>{order.obs}</span>
							</div>
						{/if}

						<div class="card-footer">
							<div class="price-payment">
								<span class="total-val">R$ {formatCurrency(order.total)}</span>
								<span class="payment-method">{order.payment}</span>
							</div>
							<div class="card-actions">
								<button 
									type="button"
									class="btn-action print-btn" 
									draggable="false"
									on:mousedown|stopPropagation
									on:click|stopPropagation={() => handlePrint(order)} 
									title="Imprimir Comanda"
								>
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="6 9 6 2 18 2 18 9"></polyline>
										<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
										<rect x="6" y="14" width="12" height="8"></rect>
									</svg>
								</button>
								<button 
									type="button"
									class="btn-action primary" 
									draggable="false"
									on:mousedown|stopPropagation
									on:click|stopPropagation={() => moveOrder(order.id, 'kitchen')}
								>
									Aceitar Pedido
								</button>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>

		<!-- Coluna 2: Na Cozinha / Em Preparo -->
		<div 
			class="column col-kitchen" 
			class:drag-over={dragOverCol === 'kitchen'}
			on:dragover={handleDragOver} 
			on:dragenter={(e) => handleDragEnter(e, 'kitchen')}
			on:dragleave={(e) => handleDragLeave(e, 'kitchen')}
			on:drop={(e) => handleDrop(e, 'kitchen')}
		>
			<div class="col-header">
				<div class="col-title">
					<span class="status-indicator blue"></span>
					<h3>Em Produção</h3>
				</div>
				<span class="count-pill">{orders.filter(o => o.status === 'kitchen').length}</span>
			</div>
			<div 
				class="cards-list"
				on:dragover={handleDragOver} 
				on:dragenter={(e) => handleDragEnter(e, 'kitchen')}
				on:drop={(e) => handleDrop(e, 'kitchen')}
			>
				{#each orders.filter(o => o.status === 'kitchen') as order (order.rawId || order.id)}
					<div 
						class="order-card" 
						class:is-being-dragged={draggedOrder && (draggedOrder.rawId === order.rawId || draggedOrder.id === order.id)}
						draggable="true" 
						on:dragstart={(e) => handleDragStart(e, order)}
						on:dragend={handleDragEnd}
						on:click={(e) => handleCardClick(order, e)}
					>
						<div class="card-top">
							{#if order.isDineInBatch}
								<span class="order-id" style="font-weight: 700; color: #1e40af;">🍽️ {order.tableLabel || order.client}</span>
							{:else}
								<span class="order-id">#{order.id}</span>
							{/if}
							<div class="card-top-badges">
								{#if order.hasEditedItem}
									<span class="origin-badge dine-in-edited" title="Item alterado pelo salão">⚠️ Alterado</span>
								{/if}
								{#if order.isDineIn}
									<span class="origin-badge dine-in" title="Consumo no Salão / Mesas"><Icon name="utensils" size={11} /> Salão</span>
								{:else if order.isWeb}
									<span class="origin-badge web" title="Cardápio Digital Web"><Icon name="globe" size={11} /> Web</span>
								{:else}
									<span class="origin-badge whatsapp" title="Pedido via WhatsApp"><Icon name="whatsapp" size={11} color="#10B981" /> WhatsApp</span>
								{/if}
								{#if order.payment_status === 'paid'}
									<span class="origin-badge pix-paid" title="PIX 100% Pago Online (Anti-Fraude)"><Icon name="check-circle" size={11} /> PIX Pago</span>
								{:else if order.rawPayment && order.rawPayment.toUpperCase().includes('PIX')}
									<span class="origin-badge pix-pending" title="Aguardando Confirmação de PIX"><Icon name="clock" size={11} /> PIX Pendente</span>
								{/if}
								<span class="order-time timer-badge">{order.time}</span>
							</div>
						</div>

						<div class="client-row">
							<span class="client-name">{order.client}</span>
							{#if order.phone && !order.isDineIn}
								<span class="client-phone">{order.phone}</span>
							{/if}
						</div>

						{#if order.hasEditedItem}
							<div class="edited-item-alert-banner" style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
								<span>⚠️ ITEM ALTERADO PELO SALÃO</span>
								{#if order.isDineInBatch}
									<button 
										type="button" 
										class="btn-rectify-card"
										on:mousedown|stopPropagation
										on:click|stopPropagation={() => handlePrintRectification(order)}
										title="Imprimir Retificação de Produção"
										style="background: #B45309; color: #fff; border: none; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; cursor: pointer; display: inline-flex; align-items: center; gap: 2px;"
									>
										🖨️ Retificação
									</button>
								{/if}
							</div>
						{/if}

						<div class="items-summary">
							{#each (order.isDineInBatch ? order.items : order.items.slice(0, 3)) as item}
								<div class="item-line" style={order.isDineInBatch ? "font-weight: 600;" : ""}>• {item}</div>
							{/each}
							{#if !order.isDineInBatch && order.items.length > 3}
								<div class="item-more">+ {order.items.length - 3} mais...</div>
							{/if}
						</div>

						{#if order.obs}
							<div class="obs-box">
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
									<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
									<circle cx="12" cy="10" r="3"></circle>
								</svg>
								<span>{order.obs}</span>
							</div>
						{/if}

						<div class="card-footer">
							<div class="price-payment">
								{#if order.isDineInBatch}
									<span class="payment-method" style="color: #3b82f6; font-weight: 600;">🍽️ Salão (Mesa)</span>
								{:else}
									<span class="total-val">R$ {formatCurrency(order.total)}</span>
									<span class="payment-method">{order.payment}</span>
								{/if}
							</div>
							<div class="card-actions">
								<button 
									type="button"
									class="btn-action print-btn" 
									draggable="false"
									on:mousedown|stopPropagation
									on:click|stopPropagation={() => handlePrint(order)} 
									title="Reimprimir Comanda"
								>
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="6 9 6 2 18 2 18 9"></polyline>
										<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
										<rect x="6" y="14" width="12" height="8"></rect>
									</svg>
								</button>
								{#if order.isDineInBatch}
									<button 
										type="button"
										class="btn-action success" 
										draggable="false"
										on:mousedown|stopPropagation
										on:click|stopPropagation={() => moveOrder(order.id, 'done')}
										title="Marcar lote como pronto"
									>
										Marcar Pronto
									</button>
								{:else}
									<button 
										type="button"
										class="btn-action success" 
										draggable="false"
										on:mousedown|stopPropagation
										on:click|stopPropagation={() => moveOrder(order.id, 'delivery')}
									>
										Despachar
									</button>
								{/if}
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>

		<!-- Coluna 3: Pronto para Entrega / A Caminho -->
		<div 
			class="column col-delivery" 
			class:drag-over={dragOverCol === 'delivery'}
			on:dragover={handleDragOver} 
			on:dragenter={(e) => handleDragEnter(e, 'delivery')}
			on:dragleave={(e) => handleDragLeave(e, 'delivery')}
			on:drop={(e) => handleDrop(e, 'delivery')}
		>
			<div class="col-header">
				<div class="col-title">
					<span class="status-indicator purple"></span>
					<h3>Saiu para Entrega</h3>
				</div>
				<span class="count-pill">{orders.filter(o => o.status === 'delivery').length}</span>
			</div>
			<div 
				class="cards-list"
				on:dragover={handleDragOver} 
				on:dragenter={(e) => handleDragEnter(e, 'delivery')}
				on:drop={(e) => handleDrop(e, 'delivery')}
			>
				{#each orders.filter(o => o.status === 'delivery') as order (order.rawId || order.id)}
					<div 
						class="order-card" 
						class:is-being-dragged={draggedOrder && (draggedOrder.rawId === order.rawId || draggedOrder.id === order.id)}
						draggable="true" 
						on:dragstart={(e) => handleDragStart(e, order)}
						on:dragend={handleDragEnd}
						on:click={(e) => handleCardClick(order, e)}
					>
						<div class="card-top">
							<span class="order-id">#{order.id}</span>
							<div class="card-top-badges">
								{#if order.isDineIn}
									<span class="origin-badge dine-in" title="Consumo no Salão / Mesas"><Icon name="utensils" size={11} /> Salão</span>
								{:else if order.isWeb}
									<span class="origin-badge web" title="Cardápio Digital Web"><Icon name="globe" size={11} /> Web</span>
								{:else}
									<span class="origin-badge whatsapp" title="Pedido via WhatsApp"><Icon name="whatsapp" size={11} color="#10B981" /> WhatsApp</span>
								{/if}
								{#if order.payment_status === 'paid'}
									<span class="origin-badge pix-paid" title="PIX 100% Pago Online (Anti-Fraude)"><Icon name="check-circle" size={11} /> PIX Pago</span>
								{:else if order.rawPayment && order.rawPayment.toUpperCase().includes('PIX')}
									<span class="origin-badge pix-pending" title="Aguardando Confirmação de PIX"><Icon name="clock" size={11} /> PIX Pendente</span>
								{/if}
								<span class="order-time">{order.time}</span>
							</div>
						</div>

						<div class="client-row">
							<span class="client-name">{order.client}</span>
							{#if order.phone && !order.isDineIn}
								<span class="client-phone">{order.phone}</span>
							{/if}
						</div>

						<div class="motoboy-badge">
							<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<rect x="1" y="3" width="15" height="13"></rect>
								<polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
								<circle cx="5.5" cy="18.5" r="2.5"></circle>
								<circle cx="18.5" cy="18.5" r="2.5"></circle>
							</svg>
							<span>{order.motoboy || (order.isDineIn ? 'Atendimento Garçom' : 'Entrega Própria')}</span>
						</div>

						<div class="items-summary">
							{#each order.items.slice(0, 3) as item}
								<div class="item-line">• {item}</div>
							{/each}
							{#if order.items.length > 3}
								<div class="item-more">+ {order.items.length - 3} mais...</div>
							{/if}
						</div>

						<div class="card-footer">
							<div class="price-payment">
								<span class="total-val">R$ {formatCurrency(order.total)}</span>
								<span class="payment-method">{order.payment}</span>
							</div>
							<div class="card-actions">
								<button 
									type="button"
									class="btn-action print-btn" 
									draggable="false"
									on:mousedown|stopPropagation
									on:click|stopPropagation={() => handlePrint(order)} 
									title="Reimprimir Comanda"
								>
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="6 9 6 2 18 2 18 9"></polyline>
										<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
										<rect x="6" y="14" width="12" height="8"></rect>
									</svg>
								</button>
								<button 
									type="button"
									class="btn-action finish" 
									draggable="false"
									on:mousedown|stopPropagation
									on:click|stopPropagation={() => moveOrder(order.id, 'done')}
								>
									Concluir
								</button>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>

		<!-- Coluna 4: Concluído -->
		<div 
			class="column col-done" 
			class:drag-over={dragOverCol === 'done'}
			on:dragover={handleDragOver} 
			on:dragenter={(e) => handleDragEnter(e, 'done')}
			on:dragleave={(e) => handleDragLeave(e, 'done')}
			on:drop={(e) => handleDrop(e, 'done')}
		>
			<div class="col-header">
				<div class="col-title">
					<span class="status-indicator green"></span>
					<h3>Concluídos</h3>
				</div>
				<div class="col-header-badges">
					{#if archivedDoneCount > 0}
						<button 
							type="button" 
							class="btn-toggle-archived {showAllCompleted ? 'active' : ''}" 
							on:click={() => showAllCompleted = !showAllCompleted}
							title={showAllCompleted ? "Ocultar pedidos com mais de 2h" : `Exibir +${archivedDoneCount} anteriores`}
						>
							<Icon name="clock" size={11} />
							<span>{showAllCompleted ? "Recolher" : `+${archivedDoneCount}`}</span>
						</button>
					{/if}
					<span class="count-pill" title="{visibleDoneOrders.length} visíveis de {allDoneOrders.length} concluídos">{visibleDoneOrders.length}</span>
				</div>
			</div>
			<div 
				class="cards-list"
				on:dragover={handleDragOver} 
				on:dragenter={(e) => handleDragEnter(e, 'done')}
				on:drop={(e) => handleDrop(e, 'done')}
			>
				{#each visibleDoneOrders as order (order.rawId || order.id)}
					<div 
						class="order-card completed" 
						class:is-being-dragged={draggedOrder && (draggedOrder.rawId === order.rawId || draggedOrder.id === order.id)}
						draggable="true" 
						on:dragstart={(e) => handleDragStart(e, order)}
						on:dragend={handleDragEnd}
						on:click={(e) => handleCardClick(order, e)}
					>
						<div class="card-top">
							{#if order.isDineInBatch}
								<span class="order-id" style="font-weight: 700; color: #1e40af;">🍽️ {order.tableLabel || order.client}</span>
							{:else}
								<span class="order-id">#{order.id}</span>
							{/if}
							<div class="card-top-badges">
								{#if order.hasEditedItem}
									<span class="origin-badge dine-in-edited" title="Item alterado pelo salão">⚠️ Alterado</span>
								{/if}
								{#if order.isDineIn}
									<span class="origin-badge dine-in" title="Consumo no Salão / Mesas"><Icon name="utensils" size={11} /> Salão</span>
								{:else if order.isWeb}
									<span class="origin-badge web" title="Cardápio Digital Web"><Icon name="globe" size={11} /> Web</span>
								{:else}
									<span class="origin-badge whatsapp" title="Pedido via WhatsApp"><Icon name="whatsapp" size={11} color="#10B981" /> WhatsApp</span>
								{/if}
								{#if order.isDineInBatch}
									<span class="origin-badge pix-paid" title="Lote pronto na cozinha"><Icon name="check-circle" size={11} /> Pronto</span>
								{:else if order.payment_status === 'paid'}
									<span class="origin-badge pix-paid" title="PIX 100% Pago Online (Anti-Fraude)"><Icon name="check-circle" size={11} /> PIX Pago</span>
								{:else if order.rawPayment && order.rawPayment.toUpperCase().includes('PIX')}
									<span class="origin-badge pix-pending" title="Aguardando Confirmação de PIX"><Icon name="clock" size={11} /> PIX Pendente</span>
								{/if}
								<span class="order-time">{order.time}</span>
							</div>
						</div>

						<div class="client-row">
							<span class="client-name">{order.client}</span>
							{#if order.phone && !order.isDineIn}
								<span class="client-phone">{order.phone}</span>
							{/if}
						</div>

						{#if order.hasEditedItem}
							<div class="edited-item-alert-banner" style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
								<span>⚠️ ITEM ALTERADO PELO SALÃO</span>
								{#if order.isDineInBatch}
									<button 
										type="button" 
										class="btn-rectify-card"
										on:mousedown|stopPropagation
										on:click|stopPropagation={() => handlePrintRectification(order)}
										title="Imprimir Retificação de Produção"
										style="background: #B45309; color: #fff; border: none; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; cursor: pointer; display: inline-flex; align-items: center; gap: 2px;"
									>
										🖨️ Retificação
									</button>
								{/if}
							</div>
						{/if}

						<div class="items-summary">
							{#each order.items.slice(0, 3) as item}
								<div class="item-line">• {item}</div>
							{/each}
							{#if order.items.length > 3}
								<div class="item-more">+ {order.items.length - 3} mais...</div>
							{/if}
						</div>

						<div class="card-footer">
							{#if order.isDineInBatch}
								<span class="payment-method" style="color: #059669; font-weight: 700;">🟢 Pronto no Salão</span>
							{:else}
								<span class="total-val">R$ {formatCurrency(order.total)}</span>
							{/if}
							<div class="card-actions">
								<button 
									type="button"
									class="btn-action print-btn" 
									draggable="false"
									on:mousedown|stopPropagation
									on:click|stopPropagation={() => handlePrint(order)} 
									title="Reimprimir Comanda"
								>
									<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="6 9 6 2 18 2 18 9"></polyline>
										<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
										<rect x="6" y="14" width="12" height="8"></rect>
									</svg>
								</button>
								{#if order.isDineInBatch}
									<span class="badge-done" style="background: #ecfdf5; color: #059669;"><Icon name="check" size={12} /> Pronto</span>
								{:else if order.rawStatus === 'cancelled'}
									<span class="badge-done" style="color: #dc2626;"><Icon name="x" size={12} /> Cancelado</span>
								{:else}
									<span class="badge-done"><Icon name="check" size={12} /> Entregue</span>
								{/if}
							</div>
						</div>
					</div>
				{/each}

				{#if archivedDoneCount > 0}
					<div class="archived-notice-card">
						{#if !showAllCompleted}
							<div class="archived-notice-content">
								<Icon name="clock" size={13} color="#64748B" />
								<span>{archivedDoneCount} {archivedDoneCount === 1 ? 'pedido anterior' : 'pedidos anteriores'} (+2h) ocultados do painel ativo.</span>
							</div>
							<button type="button" class="btn-archived-action" on:click={() => showAllCompleted = true}>
								Ver todos do turno ({allDoneOrders.length})
							</button>
						{:else}
							<div class="archived-notice-content">
								<Icon name="check" size={13} color="#15803D" />
								<span>Exibindo todos os {allDoneOrders.length} concluídos</span>
							</div>
							<button type="button" class="btn-archived-action secondary" on:click={() => showAllCompleted = false}>
								Recolher anteriores (+2h)
							</button>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</div>
</div>

<!-- MODAL DE DETALHES COMPLETOS DO PEDIDO -->
{#if selectedOrderModal}
	<div class="modal-backdrop" on:click={closeOrderModal}>
		<div class="modal-content" on:click|stopPropagation>
			<div class="modal-header">
				<div class="modal-title-box">
					<div class="order-headline">
						<h2>Pedido #{selectedOrderModal.id}</h2>
						{#if selectedOrderModal.isDineIn}
							<span class="origin-badge dine-in large"><Icon name="utensils" size={13} /> Salão / Mesas</span>
						{:else if selectedOrderModal.isWeb}
							<span class="origin-badge web large"><Icon name="globe" size={13} /> Cardápio Online</span>
						{:else}
							<span class="origin-badge whatsapp large"><Icon name="whatsapp" size={13} color="#10B981" /> WhatsApp Delivery</span>
						{/if}
					</div>
					<span class="modal-subtitle">Criado às {selectedOrderModal.time}</span>
				</div>
				<button type="button" class="btn-close-modal" on:click={closeOrderModal}><Icon name="x" size={16} /></button>
			</div>

			<div class="modal-body">
				{#if selectedOrderModal.hasEditedItem}
					<div class="edited-item-alert-banner large" style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
						<span>⚠️ ITEM ALTERADO PELO SALÃO — Verifique as alterações nos itens antes de produzir!</span>
						{#if selectedOrderModal.isDineInBatch}
							<button 
								type="button" 
								class="btn-rectify-modal"
								on:click={() => handlePrintRectification(selectedOrderModal)}
								title="Imprimir Retificação de Produção"
								style="background: #B45309; color: #fff; border: none; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: bold; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;"
							>
								🖨️ Imprimir Retificação
							</button>
						{/if}
					</div>
				{/if}

				<!-- Cliente & Endereço -->
				<div class="modal-section customer-box">
					<div class="info-row">
						<span class="info-label">Cliente:</span>
						<span class="info-value highlight">{selectedOrderModal.client}</span>
					</div>
					{#if selectedOrderModal.phone}
						<div class="info-row">
							<span class="info-label">Telefone:</span>
							<div class="phone-action-box">
								<span class="info-value">{selectedOrderModal.phone}</span>
								{#if !selectedOrderModal.isDineIn}
									<a 
										href="https://wa.me/{selectedOrderModal.phone.replace(/\D/g, '')}" 
										target="_blank" 
										class="btn-wa-direct"
									>
										Abrir no WhatsApp <Icon name="whatsapp" size={12} color="#10B981" />
									</a>
								{/if}
							</div>
						</div>
					{/if}
					{#if selectedOrderModal.obs}
						<div class="info-row">
							<span class="info-label">Local / Obs:</span>
							<span class="info-value">{selectedOrderModal.obs}</span>
						</div>
					{/if}
				</div>

				<!-- Itens do Pedido -->
				<div class="modal-section items-section">
					<h3 class="section-title">Itens Solicitados</h3>
					<div class="items-list-box">
						{#each selectedOrderModal.items as it}
							<div class="item-detail-row">
								<span class="item-name">{it}</span>
							</div>
						{/each}
					</div>
				</div>

				<!-- Resumo Financeiro & PIX Anti-Fraude -->
				<div class="modal-section summary-section">
					<div class="summary-line">
						<span>Forma de Pagamento:</span>
						<strong>{selectedOrderModal.payment}</strong>
					</div>
					<div class="summary-line" style="margin-top: 8px; padding: 8px 12px; border-radius: 8px; background: {selectedOrderModal.payment_status === 'paid' ? '#F0FDF4' : '#FEF3C7'}; border: 1px solid {selectedOrderModal.payment_status === 'paid' ? '#86EFAC' : '#FDE68A'}; display: flex; align-items: center; justify-content: space-between;">
						<span style="font-weight: 800; font-size: 13px; color: {selectedOrderModal.payment_status === 'paid' ? '#166534' : '#92400E'}; display: flex; align-items: center; gap: 6px;">
							{#if selectedOrderModal.payment_status === 'paid'}
								<Icon name="check-circle" size={14} /> PIX 100% PAGO ONLINE
							{:else}
								<Icon name="clock" size={14} /> AGUARDANDO PAGAMENTO PIX
							{/if}
						</span>
						<button 
							type="button" 
							on:click={() => toggleOrderPaymentStatus(selectedOrderModal)}
							style="background: {selectedOrderModal.payment_status === 'paid' ? '#DC2626' : '#16A34A'}; color: #FFFFFF; border: none; padding: 5px 12px; border-radius: 6px; font-weight: 800; font-size: 12px; cursor: pointer; transition: background 0.2s;"
						>
							{#if selectedOrderModal.payment_status === 'paid'}
								Marcar Pendente
							{:else}
								<Icon name="check" size={12} /> Confirmar PIX Pago
							{/if}
						</button>
					</div>
					{#if selectedOrderModal.deliveryFee > 0}
						<div class="summary-line">
							<span>Taxa de Entrega:</span>
							<span>R$ {formatCurrency(selectedOrderModal.deliveryFee)}</span>
						</div>
					{/if}
					<div class="summary-line total-line">
						<span>Total do Pedido:</span>
						<strong class="total-highlight">R$ {formatCurrency(selectedOrderModal.total)}</strong>
					</div>
				</div>

				<!-- Mudar Etapa Diretamente pelo Modal -->
				<div class="modal-section status-actions-section">
					<h3 class="section-title">Avançar Etapa do Pedido</h3>
					<div class="status-buttons-row">
						<button 
							type="button" 
							class="btn-flow {selectedOrderModal.status === 'analysis' ? 'active yellow' : ''}"
							on:click={() => moveOrder(selectedOrderModal.id, 'analysis')}
						>
							1. Em Análise
						</button>
						<button 
							type="button" 
							class="btn-flow {selectedOrderModal.status === 'kitchen' ? 'active blue' : ''}"
							on:click={() => moveOrder(selectedOrderModal.id, 'kitchen')}
						>
							2. Na Cozinha
						</button>
						<button 
							type="button" 
							class="btn-flow {selectedOrderModal.status === 'delivery' ? 'active purple' : ''}"
							on:click={() => moveOrder(selectedOrderModal.id, 'delivery')}
						>
							3. Em Entrega
						</button>
						<button 
							type="button" 
							class="btn-flow {selectedOrderModal.status === 'done' ? 'active green' : ''}"
							on:click={() => moveOrder(selectedOrderModal.id, 'done')}
						>
							4. Concluído
						</button>
					</div>
				</div>
			</div>

			<div class="modal-footer">
				<button 
					type="button" 
					class="btn-modal-print" 
					on:click={() => handlePrint(selectedOrderModal)}
				>
					<Icon name="printer" size={14} /> Imprimir Comanda Térmica
				</button>
				<button 
					type="button" 
					class="btn-modal-close" 
					on:click={closeOrderModal}
				>
					Fechar
				</button>
			</div>
		</div>
	</div>
{/if}

<!-- MODAL RÁPIDO DE DESPACHO COM A ROLETA DE MOTOBOYS -->
{#if showDispatchModal && orderToDispatch}
	<div class="modal-backdrop" on:click|self={closeDispatchModal}>
		<div class="modal-content dispatch-modal">
			<div class="modal-header">
				<div class="order-headline">
					<span class="status-indicator purple"></span>
					<h2>Despachar Pedido #{orderToDispatch.id}</h2>
				</div>
				<button type="button" class="btn-close-modal" on:click={closeDispatchModal}><Icon name="x" size={16} /></button>
			</div>

			<div class="modal-body">
				<div class="dispatch-client-box">
					<div class="dispatch-client-name">{orderToDispatch.client}</div>
					<div class="dispatch-client-addr">{orderToDispatch.obs || 'Entrega em Domicílio'}</div>
					<div class="dispatch-client-tot">Total: R$ {formatCurrency(orderToDispatch.total)} ({orderToDispatch.payment})</div>
				</div>

				<div class="dispatch-select-section">
					<h3 class="section-title">Selecione o Entregador da Roleta:</h3>

					{#if couriersList.length === 0}
						<div class="empty-couriers-note">
							Nenhum entregador cadastrado. O pedido será despachado como "Entrega Própria".
						</div>
					{:else}
						<div class="couriers-pick-list">
							{#each couriersList as c}
								<label class="courier-pick-row {selectedMotoboyId === c.id ? 'selected' : ''} {c.queue_position === 1 && c.status === 'available' ? 'is-next' : ''}">
									<div class="courier-pick-left">
										<input type="radio" name="dispatchMotoboy" value={c.id} bind:group={selectedMotoboyId}>
										<div>
											<div class="pick-name">
												<strong>{c.name}</strong>
												{#if c.queue_position === 1 && c.status === 'available'}
													<span class="next-tag-badge">Próximo da Vez</span>
												{/if}
											</div>
											<div class="pick-sub">
												{c.vehicle_type === 'bike' ? 'Bike' : 'Moto'} {c.plate ? `• ${c.plate}` : ''} • Taxa: R$ {formatCurrency(c.default_fee)}
											</div>
										</div>
									</div>
									<span class="status-indicator-pill {c.status}">
										{c.status === 'available' ? `Fila #${c.queue_position}` : c.status === 'busy' ? 'Na rua' : 'Pausa'}
									</span>
								</label>
							{/each}
						</div>
					{/if}
				</div>
			</div>

			<div class="modal-footer" style="justify-content: space-between;">
				<button type="button" class="btn-modal-close" on:click={dispatchWithoutMotoboy}>
					Despachar sem Motoboy
				</button>
				<button type="button" class="btn-action success" style="padding:10px 20px; font-size:13px;" disabled={isDispatching} on:click={confirmDispatch}>
					{#if isDispatching}
						Despachando...
					{:else}
						<Icon name="send" size={14} /> Confirmar Despacho
					{/if}
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	/* Dispatch Modal Styles */
	.dispatch-modal {
		max-width: 480px;
	}

	.dispatch-client-box {
		background: #F8FAFC;
		border: 1px solid #E2E8F0;
		border-radius: 10px;
		padding: 12px 16px;
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	.dispatch-client-name {
		font-weight: 800;
		color: #0F172A;
		font-size: 15px;
	}

	.dispatch-client-addr {
		font-size: 12.5px;
		color: #64748B;
	}

	.dispatch-client-tot {
		font-size: 12px;
		font-weight: 700;
		color: #FF6A00;
		margin-top: 2px;
	}

	.dispatch-select-section {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.couriers-pick-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		max-height: 240px;
		overflow-y: auto;
	}

	.courier-pick-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 14px;
		border: 1.5px solid #E2E8F0;
		border-radius: 10px;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.courier-pick-row:hover {
		background: #F8FAFC;
		border-color: #CBD5E1;
	}

	.courier-pick-row.selected {
		background: #FFF9F5;
		border-color: #FF6A00;
	}

	.courier-pick-row.is-next {
		box-shadow: 0 0 0 1px #FF6A00;
	}

	.courier-pick-left {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.pick-name {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 13.5px;
		color: #0F172A;
	}

	.next-tag-badge {
		font-size: 10px;
		font-weight: 800;
		background: #FF6A00;
		color: #FFF;
		padding: 2px 6px;
		border-radius: 4px;
	}

	.pick-sub {
		font-size: 11.5px;
		color: #64748B;
	}

	.status-indicator-pill {
		font-size: 11px;
		font-weight: 700;
		padding: 3px 8px;
		border-radius: 6px;
	}

	.status-indicator-pill.available { background: #ECFDF5; color: #065F46; }
	.status-indicator-pill.busy { background: #EFF6FF; color: #1E40AF; }
	.status-indicator-pill.offline { background: #F1F5F9; color: #64748B; }

	.empty-couriers-note {
		font-size: 13px;
		color: #64748B;
		padding: 12px;
		text-align: center;
		background: #F8FAFC;
		border-radius: 8px;
	}

	.kanban-wrapper {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		background: var(--neutral-bg, #F8FAFC);
		overflow: hidden;
	}

	.kanban-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 20px;
		background: var(--neutral-surface, #FFFFFF);
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		gap: 16px;
		flex-wrap: wrap;
	}

	.title-area {
		display: flex;
		align-items: baseline;
		gap: 10px;
	}

	.title-area h2 {
		font-size: 17px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
		margin: 0;
		letter-spacing: -0.2px;
	}

	.live-pill {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		font-size: 10.5px;
		font-weight: 500;
		color: var(--semantic-success, #059669);
		background: var(--semantic-success-soft, #ECFDF5);
		border: 1px solid rgba(5, 150, 105, 0.2);
		padding: 2px 8px;
		border-radius: var(--radius-full, 9999px);
	}

	.live-pill .dot {
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: var(--semantic-success, #059669);
	}

	/* Faixa Operacional Integrada de Métricas */
	.metrics-row {
		display: flex;
		align-items: center;
		background: var(--neutral-bg, #F8FAFC);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		padding: 3px 4px;
		gap: 0;
	}

	.metric-card {
		display: flex;
		flex-direction: column;
		padding: 3px 14px;
		background: transparent;
		border: none;
		border-right: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: 0;
	}

	.metric-card:last-child {
		border-right: none;
	}

	.metric-label {
		font-size: 9.5px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		color: var(--text-muted, #94A3B8);
	}

	.metric-value {
		font-size: 13px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
		line-height: 1.2;
		margin-top: 1px;
	}

	.metric-value.green {
		color: var(--semantic-success, #059669);
	}

	/* Quadro Kanban de Colunas */
	.kanban-board {
		flex: 1;
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 14px;
		padding: 14px 20px;
		overflow-x: auto;
		overflow-y: hidden;
	}

	.column {
		background: #EEF2F6;
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-md, 10px);
		display: flex;
		flex-direction: column;
		overflow: hidden;
		min-width: 280px;
		transition: background 0.15s ease, border-color 0.15s ease;
	}

	.column.drag-over {
		background: rgba(255, 106, 0, 0.04) !important;
		border-color: rgba(255, 106, 0, 0.35) !important;
		border-style: dashed !important;
		box-shadow: inset 0 0 0 1px rgba(255, 106, 0, 0.2);
		transition: background 0.15s ease, border-color 0.15s ease;
	}

	.col-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 14px;
		background: var(--neutral-surface, #FFFFFF);
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
	}

	.col-title {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.status-indicator {
		width: 8px;
		height: 8px;
		border-radius: 50%;
	}

	.status-indicator.yellow { background: var(--semantic-warning, #D97706); }
	.status-indicator.blue { background: var(--semantic-info, #2563EB); }
	.status-indicator.purple { background: #7C3AED; }
	.status-indicator.green { background: var(--semantic-success, #059669); }

	.col-title h3 {
		font-size: 13px;
		font-weight: 600;
		color: var(--text-primary, #0F172A);
		margin: 0;
	}

	.count-pill {
		font-size: 11px;
		font-weight: 600;
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-secondary, #475569);
		padding: 1px 7px;
		border-radius: var(--radius-full, 9999px);
		border: 1px solid var(--neutral-border, #E2E8F0);
	}

	.cards-list {
		flex: 1;
		padding: 10px;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.cards-list::-webkit-scrollbar {
		width: 4px;
	}

	.cards-list::-webkit-scrollbar-thumb {
		background: #CBD5E1;
		border-radius: 4px;
	}

	/* Card do Pedido com Hierarquia Visual Refinada */
	.order-card {
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		padding: 11px 12px;
		box-shadow: var(--shadow-sm, 0 1px 2px 0 rgba(15, 23, 42, 0.05));
		display: flex;
		flex-direction: column;
		gap: 7px;
		transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
		cursor: pointer;
		user-select: none;
		position: relative;
		animation: slideInCard 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
	}

	@keyframes slideInCard {
		from {
			opacity: 0;
			transform: translateY(10px) scale(0.98);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	.order-card:hover {
		transform: translateY(-1px);
		box-shadow: var(--shadow-card-hover, 0 4px 6px -1px rgba(15, 23, 42, 0.08));
		border-color: #CBD5E1;
	}

	.order-card.is-being-dragged {
		opacity: 0.55;
		transform: rotate(1.5deg) scale(1.02);
		box-shadow: 0 14px 36px rgba(0, 0, 0, 0.18);
		border-color: var(--brand-primary, #FF6A00);
		z-index: 10;
	}

	.pulse-border {
		border-left: 3px solid var(--semantic-warning, #D97706);
		animation: slideInCard 0.28s cubic-bezier(0.16, 1, 0.3, 1) both, pulseBorder 2s ease-in-out infinite;
	}

	@keyframes pulseBorder {
		0%, 100% { border-left-color: var(--semantic-warning, #D97706); }
		50% { border-left-color: rgba(217, 119, 6, 0.35); }
	}

	.card-top {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 6px;
	}

	.order-id {
		font-size: 12.5px;
		font-weight: 700;
		color: var(--brand-primary, #FF6A00);
		letter-spacing: -0.2px;
	}

	.card-top-badges {
		display: flex;
		align-items: center;
		gap: 5px;
		flex-wrap: wrap;
	}

	.origin-badge {
		font-size: 10px;
		font-weight: 600;
		padding: 1px 6px;
		border-radius: 4px;
		display: inline-flex;
		align-items: center;
		gap: 3px;
		line-height: 1.3;
	}

	.origin-badge.whatsapp {
		background: var(--semantic-success-soft, #ECFDF5);
		color: var(--semantic-success, #059669);
		border: 1px solid rgba(5, 150, 105, 0.2);
	}

	.origin-badge.dine-in {
		background: var(--semantic-info-soft, #EFF6FF);
		color: var(--semantic-info, #2563EB);
		border: 1px solid rgba(37, 99, 235, 0.2);
	}

	.origin-badge.dine-in-edited {
		background: #FEF3C7;
		color: #B45309;
		border: 1px solid #F59E0B;
		font-weight: 700;
	}

	.edited-item-alert-banner {
		background: #FFFBEB;
		border: 1px solid #FCD34D;
		color: #B45309;
		font-size: 11px;
		font-weight: 700;
		padding: 4px 8px;
		border-radius: 6px;
		margin: 6px 0;
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.edited-item-alert-banner.large {
		font-size: 13px;
		padding: 10px 14px;
		margin-bottom: 16px;
		border-radius: 8px;
	}

	.origin-badge.web {
		background: #FAF5FF;
		color: #7C3AED;
		border: 1px solid rgba(124, 58, 237, 0.2);
	}

	.origin-badge.pix-paid {
		background: var(--semantic-success-soft, #ECFDF5);
		color: var(--semantic-success, #059669);
		border: 1px solid rgba(5, 150, 105, 0.25);
		font-weight: 600;
	}

	.origin-badge.pix-pending {
		background: var(--semantic-warning-soft, #FFFBEB);
		color: var(--semantic-warning, #D97706);
		border: 1px dashed rgba(217, 119, 6, 0.4);
		font-weight: 600;
	}

	.origin-badge.large {
		font-size: 11.5px;
		padding: 3px 8px;
		border-radius: 6px;
	}

	.order-time {
		font-size: 10.5px;
		color: var(--text-muted, #94A3B8);
		font-weight: 500;
		margin-left: 2px;
	}

	.timer-badge {
		background: var(--semantic-info-soft, #EFF6FF);
		color: var(--semantic-info, #2563EB);
		padding: 1px 5px;
		border-radius: 4px;
		font-weight: 600;
	}

	/* Linha do Cliente */
	.client-row {
		display: flex;
		flex-direction: column;
		gap: 1px;
	}

	.client-name {
		font-size: 13px;
		font-weight: 600;
		color: var(--text-primary, #0F172A);
		line-height: 1.3;
	}

	.client-phone {
		font-size: 11px;
		color: var(--text-muted, #94A3B8);
		font-weight: 400;
	}

	.motoboy-badge {
		font-size: 10.5px;
		font-weight: 500;
		background: #FAF5FF;
		color: #7C3AED;
		border: 1px solid rgba(124, 58, 237, 0.15);
		padding: 2px 7px;
		border-radius: 4px;
		display: inline-flex;
		align-items: center;
		gap: 5px;
	}

	/* Resumo dos Itens */
	.items-summary {
		font-size: 11.5px;
		color: var(--text-secondary, #475569);
		line-height: 1.4;
		padding: 1px 0;
	}

	.item-line {
		margin-bottom: 1px;
	}

	.item-more {
		font-size: 10.5px;
		color: var(--text-muted, #94A3B8);
		font-style: normal;
	}

	/* Observações & Endereço */
	.obs-box {
		background: var(--neutral-surface-subtle, #F8FAFC);
		border: 1px solid var(--neutral-border, #E2E8F0);
		color: var(--text-secondary, #475569);
		font-size: 11px;
		padding: 5px 8px;
		border-radius: var(--radius-sm, 6px);
		display: flex;
		align-items: flex-start;
		gap: 6px;
	}

	/* Rodapé do Card */
	.card-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-top: 8px;
		border-top: 1px solid var(--neutral-border-subtle, #F1F5F9);
		margin-top: 2px;
	}

	.price-payment {
		display: flex;
		flex-direction: column;
	}

	.total-val {
		font-size: 13.5px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
	}

	.payment-method {
		display: block;
		font-size: 10px;
		color: var(--text-muted, #94A3B8);
	}

	.card-actions {
		display: flex;
		gap: 6px;
		align-items: center;
	}

	.btn-action {
		padding: 5px 11px;
		border-radius: var(--radius-sm, 6px);
		font-size: 11.5px;
		font-weight: 600;
		cursor: pointer;
		border: 1px solid transparent;
		transition: background 0.15s ease, border-color 0.15s ease;
		font-family: inherit;
		user-select: none;
	}

	.btn-action.primary {
		background: var(--brand-primary, #FF6A00);
		color: #FFFFFF;
	}

	.btn-action.primary:hover {
		background: var(--brand-primary-hover, #E85A00);
	}

	.btn-action.success {
		background: var(--semantic-success, #059669);
		color: #FFFFFF;
	}

	.btn-action.success:hover {
		background: #047857;
	}

	.btn-action.finish {
		background: var(--neutral-surface-hover, #F1F5F9);
		border-color: var(--neutral-border, #E2E8F0);
		color: var(--text-secondary, #475569);
	}

	.btn-action.finish:hover {
		background: var(--neutral-border, #E2E8F0);
		color: var(--text-primary, #0F172A);
	}

	.print-btn {
		background: var(--neutral-surface-hover, #F1F5F9);
		border: 1px solid var(--neutral-border, #E2E8F0);
		color: var(--text-secondary, #475569);
		padding: 5px 7px;
		border-radius: var(--radius-sm, 6px);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		transition: background 0.15s ease, color 0.15s ease;
	}

	.print-btn:hover {
		background: var(--neutral-border, #E2E8F0);
		color: var(--text-primary, #0F172A);
	}

	.order-card.completed {
		opacity: 0.85;
	}

	.badge-done {
		font-size: 11px;
		font-weight: 600;
		color: var(--semantic-success, #059669);
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}

	/* MODAL DE DETALHES */
	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		height: 100dvh;
		background: rgba(15, 23, 42, 0.7);
		backdrop-filter: blur(4px);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999;
		padding: 16px;
	}

	.modal-content {
		background: var(--neutral-surface, #FFFFFF);
		width: 100%;
		max-width: 520px;
		border-radius: var(--radius-lg, 16px);
		box-shadow: var(--shadow-modal, 0 20px 25px -5px rgba(15, 23, 42, 0.15));
		display: flex;
		flex-direction: column;
		max-height: 90vh;
		overflow: hidden;
		animation: modalScaleIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
	}

	@keyframes modalScaleIn {
		from { opacity: 0; transform: scale(0.96); }
		to { opacity: 1; transform: scale(1); }
	}

	.modal-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		padding: 16px 20px;
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		background: var(--neutral-bg, #F8FAFC);
	}

	.order-headline {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.order-headline h2 {
		margin: 0;
		font-size: 18px;
		font-weight: 700;
		color: var(--text-primary, #0F172A);
	}

	.modal-subtitle {
		font-size: 11.5px;
		color: var(--text-muted, #94A3B8);
		margin-top: 3px;
		display: block;
	}

	.btn-close-modal {
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		width: 30px;
		height: 30px;
		border-radius: var(--radius-sm, 6px);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 15px;
		cursor: pointer;
		color: var(--text-secondary, #475569);
		transition: all 0.15s ease;
	}

	.btn-close-modal:hover {
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-primary, #0F172A);
	}

	.modal-body {
		padding: 16px 20px;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 14px;
	}

	.modal-section {
		background: var(--neutral-bg, #F8FAFC);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: var(--radius-sm, 6px);
		padding: 12px 14px;
	}

	.customer-box {
		display: flex;
		flex-direction: column;
		gap: 7px;
	}

	.info-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		font-size: 12.5px;
	}

	.info-label {
		color: var(--text-muted, #94A3B8);
		font-weight: 500;
	}

	.info-value {
		color: var(--text-primary, #0F172A);
		font-weight: 600;
	}

	.info-value.highlight {
		color: var(--brand-primary, #FF6A00);
		font-size: 13.5px;
	}

	.phone-action-box {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.btn-wa-direct {
		background: #25D366;
		color: #FFFFFF;
		font-size: 10.5px;
		font-weight: 600;
		padding: 2px 7px;
		border-radius: 4px;
		text-decoration: none;
		transition: opacity 0.15s ease;
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}

	.btn-wa-direct:hover {
		opacity: 0.9;
	}

	.section-title {
		font-size: 12.5px;
		font-weight: 600;
		color: var(--text-primary, #0F172A);
		margin: 0 0 8px 0;
	}

	.items-list-box {
		display: flex;
		flex-direction: column;
		gap: 5px;
	}

	.item-detail-row {
		font-size: 12.5px;
		color: var(--text-secondary, #475569);
		padding: 5px 8px;
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #E2E8F0);
		border-radius: 4px;
		font-weight: 500;
	}

	.summary-section {
		display: flex;
		flex-direction: column;
		gap: 7px;
		font-size: 12.5px;
	}

	.summary-line {
		display: flex;
		justify-content: space-between;
		color: var(--text-secondary, #475569);
	}

	.summary-line.total-line {
		border-top: 1px solid var(--neutral-border, #E2E8F0);
		padding-top: 8px;
		margin-top: 3px;
		font-size: 14px;
	}

	.total-highlight {
		color: var(--text-primary, #0F172A);
		font-size: 16px;
	}

	.status-buttons-row {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 8px;
	}

	.btn-flow {
		padding: 8px;
		font-size: 11.5px;
		font-weight: 600;
		border-radius: var(--radius-sm, 6px);
		border: 1px solid var(--neutral-border, #E2E8F0);
		background: var(--neutral-surface, #FFFFFF);
		color: var(--text-secondary, #475569);
		cursor: pointer;
		transition: all 0.15s ease;
		font-family: inherit;
	}

	.btn-flow:hover {
		background: var(--neutral-surface-hover, #F1F5F9);
	}

	.btn-flow.active.yellow {
		background: var(--semantic-warning-soft, #FFFBEB);
		border-color: var(--semantic-warning, #D97706);
		color: var(--semantic-warning, #D97706);
	}

	.btn-flow.active.blue {
		background: var(--semantic-info-soft, #EFF6FF);
		border-color: var(--semantic-info, #2563EB);
		color: var(--semantic-info, #2563EB);
	}

	.btn-flow.active.purple {
		background: #F5F3FF;
		border-color: #7C3AED;
		color: #7C3AED;
	}

	.btn-flow.active.green {
		background: var(--semantic-success-soft, #ECFDF5);
		border-color: var(--semantic-success, #059669);
		color: var(--semantic-success, #059669);
	}

	.modal-footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 10px;
		padding: 14px 20px;
		background: var(--neutral-bg, #F8FAFC);
		border-top: 1px solid var(--neutral-border, #E2E8F0);
	}

	.btn-modal-print {
		background: var(--text-primary, #0F172A);
		color: #FFFFFF;
		border: none;
		padding: 8px 16px;
		border-radius: var(--radius-sm, 6px);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.15s ease;
		font-family: inherit;
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}

	.btn-modal-print:hover {
		background: #1E293B;
	}

	.btn-modal-close {
		background: var(--neutral-surface, #FFFFFF);
		color: var(--text-secondary, #475569);
		border: 1px solid var(--neutral-border, #E2E8F0);
		padding: 8px 16px;
		border-radius: var(--radius-sm, 6px);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		font-family: inherit;
		transition: background 0.15s ease;
	}

	.btn-modal-close:hover {
		background: var(--neutral-surface-hover, #F1F5F9);
	}

	/* ========================================================
	   TOUCH TARGETS & ERGONOMIA TOUCH (KANBAN)
	   ======================================================== */
	@media (max-width: 1023px) {
		.btn-close-modal {
			width: 38px;
			height: 38px;
			border-radius: var(--radius-sm, 6px);
		}

		.btn-flow {
			min-height: 42px;
			padding: 8px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			font-size: 12px;
			-webkit-tap-highlight-color: transparent;
		}

		.btn-flow.active {
			box-shadow: 0 0 0 1px currentColor;
			font-weight: 700;
		}

		.btn-modal-print,
		.btn-modal-close {
			min-height: 42px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
		}

		.card-actions .btn-action {
			min-height: 34px;
			padding: 6px 12px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			-webkit-tap-highlight-color: transparent;
		}

		.card-actions .print-btn {
			min-height: 34px;
			min-width: 34px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			-webkit-tap-highlight-color: transparent;
		}
	}

	@media (max-width: 600px) {
		.modal-backdrop {
			padding: 12px;
		}

		.modal-content {
			max-height: 94vh;
			max-height: 94dvh;
			border-radius: var(--radius-md, 10px);
		}

		.modal-header {
			padding: 14px 16px;
		}

		.modal-body {
			padding: 14px 16px;
			gap: 12px;
		}

		.modal-footer {
			padding: 12px 16px;
			flex-wrap: wrap;
		}
	}

	/* ========================================================
	   AUTO-RECOLHIMENTO DE CONCLUÍDOS (+2 HORAS / DIA ANTERIOR)
	   ======================================================== */
	.col-header-badges {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.btn-toggle-archived {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		background: #F1F5F9;
		border: 1px solid #CBD5E1;
		color: #475569;
		padding: 2px 7px;
		border-radius: 6px;
		font-size: 11px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-toggle-archived:hover {
		background: #E2E8F0;
		color: #0F172A;
		border-color: #94A3B8;
	}

	.btn-toggle-archived.active {
		background: #FFF7ED;
		border-color: #FED7AA;
		color: #EA580C;
	}

	.archived-notice-card {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 12px 14px;
		margin-top: 6px;
		background: #F8FAFC;
		border: 1px dashed #CBD5E1;
		border-radius: var(--radius-md, 8px);
		text-align: center;
	}

	.archived-notice-content {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 11px;
		color: #64748B;
		line-height: 1.3;
	}

	.btn-archived-action {
		background: #FFFFFF;
		border: 1px solid #CBD5E1;
		border-radius: 6px;
		padding: 5px 12px;
		font-size: 11px;
		font-weight: 700;
		color: #334155;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-archived-action:hover {
		background: #F1F5F9;
		border-color: #94A3B8;
		color: #0F172A;
	}

	.btn-archived-action.secondary {
		background: transparent;
		border-color: transparent;
		color: #64748B;
		text-decoration: underline;
		padding: 2px 8px;
	}

	.btn-archived-action.secondary:hover {
		color: #0F172A;
	}

	.reconciliation-alert-container {
		display: flex;
		flex-direction: column;
		gap: 10px;
		margin-bottom: 20px;
		width: 100%;
	}

	.reconciliation-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		background: #FFFBEB;
		border: 1px solid #FDE68A;
		border-left: 6px solid #D97706;
		color: #92400E;
		padding: 14px 20px;
		border-radius: 10px;
		box-shadow: 0 3px 12px rgba(217, 119, 6, 0.12);
	}

	.rec-icon {
		font-size: 1.8rem;
		flex-shrink: 0;
	}

	.rec-body {
		flex: 1;
	}

	.rec-title {
		font-weight: 800;
		font-size: 0.95rem;
		letter-spacing: -0.01em;
		color: #B45309;
		margin-bottom: 3px;
	}

	.rec-desc {
		font-size: 0.85rem;
		line-height: 1.4;
		color: #78350F;
	}

	.rec-actions {
		display: flex;
		gap: 10px;
		flex-shrink: 0;
	}

	.btn-rec-resolve, .btn-rec-refund {
		border: none;
		border-radius: 8px;
		padding: 8px 14px;
		font-size: 0.82rem;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}

	.btn-rec-resolve {
		background: #059669;
		color: #FFFFFF;
	}

	.btn-rec-resolve:hover {
		background: #047857;
		transform: translateY(-1px);
	}

	.btn-rec-refund {
		background: #475569;
		color: #FFFFFF;
	}

	.btn-rec-refund:hover {
		background: #334155;
		transform: translateY(-1px);
	}

	.btn-rec-resolve:disabled, .btn-rec-refund:disabled {
		opacity: 0.6;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}
</style>
